import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { COCKPIT_ROLES, decodeRoles, initializeSession } from '../../lib/auth'
import type { Lang } from '../../lib/i18n'
import {
  advanceOrderStatus, fetchAwaitingFinalCheck, fetchBoardOrders, formatOrderId, isAlreadyThere, rejectOrder,
  sendFinalCheck,
  type BoardListResponse, type FinalCheckFact, type OrderStatusValue, type SendOutcome,
} from '../../lib/orders-api'
import { cancelHeld, enqueueHeld, kickDrain, releaseHeld, takeHeld } from '../../lib/idb-queue'
import { Button } from '../../components/ui/button'
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { CommandHeader } from '../../components/CommandHeader'
import { ot, STATUS_BADGE_LABEL } from '../orders/orders-i18n'
import { Ltr } from '../orders/Ltr'
import { bt, fillSlots } from './board-i18n'
import { OrderCard, type FinalCheckRowState } from './OrderCard'
import { RingPanel } from '../ring/RingPanel'
import { playNewOrderChime } from '../ring/ring-audio'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

type StatusFilter = 'ALL' | OrderStatusValue

// Board triage tabs (D6: zone filter deferred — v1 filters = status-tabs + date).
// Mirrors the full OrderStatusValue lifecycle from lib/orders-api.ts.
const STATUS_TABS: StatusFilter[] = [
  'ALL', 'PENDING', 'CONFIRMED', 'IN_KITCHEN', 'AWAITING_RETAIL_PACK', 'AWAITING_FINAL_CHECK',
  'READY_FOR_DISPATCH', 'OUT_FOR_DELIVERY', 'DELIVERED', 'CANCELLED', 'REFUNDED',
]

function tabFromState(state: unknown): StatusFilter {
  const tab = (state as { boardTab?: unknown } | null)?.boardTab
  return STATUS_TABS.includes(tab as StatusFilter) ? (tab as StatusFilter) : 'ALL'
}

const UNDO_WINDOW_S = 7

// R517 (Frank #66769, Rook #66772): a move tapped inside the Undo window is written to the queue
// AT THE TAP, held until the window ends. One sender: the screen, at the window end, after
// takeHeld consumes the entry (false = a drain already has it, so the screen sends nothing).
// Leaving the screen inside the window releases it (sendAfter cleared) and kicks one drain.
interface HeldMove {
  orderId: string
  toStatus: OrderStatusValue
  expectedStatus: OrderStatusValue
  // The queue entry's uuid; null when it could not be written (the screen then sends as before).
  held: Promise<string | null>
}

function holdMove(orderId: string, toStatus: OrderStatusValue, expectedStatus: OrderStatusValue): HeldMove {
  const held = Promise.resolve()
    .then(() => enqueueHeld('PATCH', `/api/admin/orders/${orderId}/status`, { toStatus, expectedStatus }, UNDO_WINDOW_S * 1000))
    .catch(() => null)
  return { orderId, toStatus, expectedStatus, held }
}

/** Undo (or a new tap replacing the move): the held entry is deleted, nothing is sent. */
function cancelMove(move: HeldMove): void {
  void move.held.then((uuid) => (uuid === null ? false : cancelHeld(uuid))).catch(() => undefined)
}

/** The window end: true when this screen may send (it consumed the held entry). */
async function takeMove(move: HeldMove): Promise<boolean> {
  const uuid = await move.held
  if (uuid === null) return true
  return takeHeld(uuid).catch(() => true)
}

/** Leaving inside the window: never dropped. Released to the queue, then one drain now. */
async function releaseMoves(moves: HeldMove[]): Promise<void> {
  let released = false
  await Promise.all(moves.map(async (move) => {
    const uuid = await move.held
    if (uuid === null) {
      await advanceOrderStatus(move.orderId, move.toStatus, { expectedStatus: move.expectedStatus }).catch(() => undefined)
      return
    }
    if (await releaseHeld(uuid).catch(() => false)) released = true
  }))
  if (released) await Promise.resolve().then(() => kickDrain()).catch(() => undefined)
}

// D4: 15-30s band — 20s picked as the midpoint.
const POLL_INTERVAL_MS = 20_000
const AGING_TICK_MS = 30_000

interface PendingAdvance {
  toStatus: OrderStatusValue
  secondsLeft: number
  // The card's status at the tap, sent as `expectedStatus`: what the person saw, not what a
  // poll inside the undo window has since read.
  expectedStatus: OrderStatusValue
}

// R264 (b): the one toast that names the last move's order and what became of it.
type Notice =
  | { kind: 'ok'; orderId: string }
  | { kind: 'fail'; orderId: string; message: string }
  | { kind: 'queuedRefused'; orderId: string; status: string }
  | { kind: 'kitchen'; orderId: string; count: string; toStatus: OrderStatusValue; expectedStatus: OrderStatusValue }

// A template's `{code}` is the order code, kept left-to-right inside Arabic text.
function CodedLine({ template, orderId, shortCode, slots }: {
  template: string; orderId: string; shortCode?: string | null; slots?: Record<string, string>
}) {
  const parts = template.split('{code}')
  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 && <Ltr>{formatOrderId(orderId, shortCode)}</Ltr>}
          {slots ? fillSlots(part, slots) : part}
        </span>
      ))}
    </>
  )
}

// R503 (b), Rook #67784 (1): a new order arriving plays one chime from the shared module, once,
// never repeated; an order still waiting rings through the shared ring (features/ring) for the
// reasons ring.screens gives the Board.

export function BoardScreen({ lang, onLangToggle, isOnline }: Props) {
  const [authState, setAuthState] = useState<'checking' | 'ok' | 'redirect'>('checking')
  const [roles, setRoles] = useState<string[]>([])
  // R460: the open tab rides on the Board's own history entry (location.state, not the URL and
  // not storage), so coming Back from an order shows the tab the person left.
  const location = useLocation()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(() => tabFromState(location.state))
  const chooseTab = useCallback(
    (tab: StatusFilter) => {
      setStatusFilter(tab)
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: { ...(location.state as object | null), boardTab: tab } },
      )
    },
    [navigate, location.pathname, location.search, location.state],
  )
  const [data, setData] = useState<BoardListResponse | null>(null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [newIds, setNewIds] = useState<Set<string>>(new Set())
  const [now, setNow] = useState(() => new Date())
  const [pending, setPending] = useState<Record<string, PendingAdvance>>({})
  // R197 (b): null = no readable fact read (today's two-click path, no button anywhere).
  const [facts, setFacts] = useState<Map<string, FinalCheckFact> | null>(null)
  const [fcRows, setFcRows] = useState<Record<string, FinalCheckRowState>>({})
  // Orders the server accepted: off the tab at once, held off until a poll stops listing them.
  const [sentHold, setSentHold] = useState<Set<string>>(new Set())
  // R264 (b): orders whose write is waiting for the server's reply, and the toast for the last reply.
  const [sending, setSending] = useState<Set<string>>(new Set())
  const [notice, setNotice] = useState<Notice | null>(null)

  const factsSeqRef = useRef(0)
  const fcInflightRef = useRef<Set<string>>(new Set())
  // queue uuid -> order id, for rows left queued (in memory only)
  const queuedUuidsRef = useRef<Map<string, string>>(new Map())
  const seenIdsRef = useRef<Set<string> | null>(null)
  const sendingRef = useRef<Set<string>>(new Set())
  // queue uuid -> order id, for moves left queued (in memory only)
  const queuedMovesRef = useRef<Map<string, string>>(new Map())
  const dataRef = useRef<BoardListResponse | null>(null)
  dataRef.current = data
  const timersRef = useRef<Record<string, { commit: number; tick: number; move: HeldMove }>>({})

  useEffect(() => {
    initializeSession().then(() => {
      const r = decodeRoles()
      setRoles(r)
      setAuthState(r.some((role) => (COCKPIT_ROLES as readonly string[]).includes(role)) ? 'ok' : 'redirect')
    })
  }, [])

  const load = useCallback((opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoadState('loading')
    fetchBoardOrders({ status: statusFilter === 'ALL' ? undefined : statusFilter })
      .then((res) => {
        const ids = new Set(res.items.map((o) => o.id))
        if (seenIdsRef.current) {
          const fresh = res.items.filter((o) => !seenIdsRef.current!.has(o.id))
          if (fresh.length > 0) {
            setNewIds(new Set(fresh.map((o) => o.id)))
            playNewOrderChime()
          } else {
            setNewIds(new Set())
          }
        } else {
          setNewIds(new Set())
        }
        seenIdsRef.current = ids
        // A hold lasts only while the poll still lists the order.
        setSentHold((h) => {
          const kept = new Set([...h].filter((id) => ids.has(id)))
          return kept.size === h.size ? h : kept
        })
        setData(res)
        setLoadState('ready')
      })
      .catch(() => {
        if (!opts?.silent) setLoadState('error')
      })
  }, [statusFilter])

  // R197 (b): the one-click final check reads its facts only on the final-check tab and only
  // for a session that can use them. A failed or malformed read drops every fact.
  const oneClickEligible = statusFilter === 'AWAITING_FINAL_CHECK' && (roles.includes('hop') || roles.includes('admin'))
  const loadFacts = useCallback(() => {
    const seq = ++factsSeqRef.current
    if (!oneClickEligible) {
      setFacts(null)
      return
    }
    fetchAwaitingFinalCheck()
      .then((rows) => {
        if (seq !== factsSeqRef.current) return
        if (!Array.isArray(rows)) {
          setFacts(null)
          return
        }
        const map = new Map<string, FinalCheckFact>()
        for (const row of rows) if (row && typeof row.id === 'string') map.set(row.id, row)
        setFacts(map)
      })
      .catch(() => {
        if (seq === factsSeqRef.current) setFacts(null)
      })
  }, [oneClickEligible])

  useEffect(() => {
    loadFacts()
  }, [loadFacts])

  // Manual (re-)load — initial mount, filter switch, retry button. Re-baselines "new"
  // tracking: switching tabs shows a different order subset, so comparing against the
  // previous tab's seen-ids would wrongly flag the whole new view as "new".
  useEffect(() => {
    seenIdsRef.current = null
    setNewIds(new Set())
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `load` is itself derived from
    // statusFilter; listing both would double-fire this effect on every filter change.
  }, [statusFilter])

  // D4: background poll (15-30s band) — silent, doesn't reset the new-since-last-look baseline.
  useEffect(() => {
    const id = window.setInterval(() => {
      load({ silent: true })
      loadFacts()
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [load, loadFacts])

  // D5: tick the aging clock so card timers/countdowns advance without a full data refetch.
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), AGING_TICK_MS)
    return () => window.clearInterval(id)
  }, [])

  // hop:reconcile (fired by idb-queue's drainQueue on a 400 conflict) — re-sync on conflict.
  useEffect(() => {
    const handler = () => load()
    window.addEventListener('hop:reconcile', handler)
    return () => window.removeEventListener('hop:reconcile', handler)
  }, [load])

  // R197 (b): a queued final check that the drain gave up on returns to idle and the Board
  // re-polls. idb-queue's events name the queue item by uuid (hop:reconcile carries only that),
  // and sendFinalCheck returned the uuid when it queued, so the row is found by uuid. A
  // hop:action-failed counts only when terminal; a non-terminal one is a 5xx the queue retries.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ uuid?: string; terminal?: boolean }>).detail
      const uuid = detail?.uuid
      if (typeof uuid !== 'string') return
      const orderId = queuedUuidsRef.current.get(uuid)
      if (orderId === undefined) return
      if (e.type === 'hop:action-failed' && detail.terminal !== true) return
      queuedUuidsRef.current.delete(uuid)
      setFcRows((rows) => (rows[orderId]?.kind === 'queued' ? { ...rows, [orderId]: { kind: 'idle' } } : rows))
      load({ silent: true })
      loadFacts()
    }
    window.addEventListener('hop:reconcile', handler)
    window.addEventListener('hop:action-failed', handler)
    return () => {
      window.removeEventListener('hop:reconcile', handler)
      window.removeEventListener('hop:action-failed', handler)
    }
  }, [load, loadFacts])

  // A move left queued is refused by the drain later: idb-queue's terminal hop:action-failed
  // carries the status; hop:reconcile (fired on a 400) carries only the uuid.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ uuid?: string; terminal?: boolean; status?: number }>).detail
      const uuid = detail?.uuid
      if (typeof uuid !== 'string') return
      const orderId = queuedMovesRef.current.get(uuid)
      if (orderId === undefined) return
      if (e.type === 'hop:action-failed' && detail.terminal !== true) return
      queuedMovesRef.current.delete(uuid)
      const status = e.type === 'hop:reconcile' ? 400 : detail.status
      setNotice({ kind: 'queuedRefused', orderId, status: String(status ?? '') })
      if (e.type === 'hop:action-failed') load({ silent: true })
    }
    window.addEventListener('hop:reconcile', handler)
    window.addEventListener('hop:action-failed', handler)
    return () => {
      window.removeEventListener('hop:reconcile', handler)
      window.removeEventListener('hop:action-failed', handler)
    }
  }, [load])

  // R264 (b): one order write, awaited. The order is "sending" until the reply, then the toast is
  // set and the board reloads (after the reply, never beside the send).
  const runWrite = useCallback(async (
    orderId: string,
    send: () => Promise<SendOutcome>,
    onReply: (res: SendOutcome) => Notice | null,
  ) => {
    if (sendingRef.current.has(orderId)) return
    sendingRef.current.add(orderId)
    setSending(new Set(sendingRef.current))
    setNotice(null)
    let next: Notice | null
    try {
      const res = await send()
      if (res.outcome === 'queued') queuedMovesRef.current.set(res.uuid, orderId)
      next = onReply(res)
    } catch (err) {
      next = { kind: 'fail', orderId, message: err instanceof Error ? err.message : String(err) }
    }
    sendingRef.current.delete(orderId)
    setSending(new Set(sendingRef.current))
    setNotice(next)
    load({ silent: true })
  }, [load])

  const sendMove = useCallback((
    orderId: string,
    toStatus: OrderStatusValue,
    expectedStatus: OrderStatusValue,
    overrideKitchen?: true,
  ) => runWrite(
    orderId,
    () => advanceOrderStatus(orderId, toStatus, overrideKitchen ? { expectedStatus, overrideKitchen } : { expectedStatus }),
    (res): Notice | null => {
      if (res.outcome === 'sent' || isAlreadyThere(res, toStatus)) return { kind: 'ok', orderId }
      if (res.outcome === 'queued') return null
      if (res.code === 'KITCHEN_OPEN') {
        return { kind: 'kitchen', orderId, count: String(res.details?.openMealItems ?? ''), toStatus, expectedStatus }
      }
      return { kind: 'fail', orderId, message: res.message }
    },
  ), [runWrite])

  const handleFinalCheck = useCallback((orderId: string, totalNow: string) => {
    if (fcInflightRef.current.has(orderId)) return
    fcInflightRef.current.add(orderId)
    setFcRows((r) => ({ ...r, [orderId]: { kind: 'pending' } }))
    const settle = (next: FinalCheckRowState | null) => {
      fcInflightRef.current.delete(orderId)
      setFcRows((r) => {
        const { [orderId]: _drop, ...rest } = r
        return next ? { ...rest, [orderId]: next } : rest
      })
    }
    sendFinalCheck(orderId, totalNow)
      .then((res) => {
        if (res.outcome === 'sent') {
          setSentHold((h) => new Set(h).add(orderId))
          settle(null)
        } else if (res.outcome === 'queued') {
          queuedUuidsRef.current.set(res.uuid, orderId)
          settle({ kind: 'queued' })
        } else {
          settle({ kind: 'rejected', message: res.message })
        }
      })
      .catch((err: unknown) => {
        settle({ kind: 'rejected', message: err instanceof Error ? err.message : String(err) })
      })
  }, [])

  // Clear all per-order undo timers on unmount. R517: a move still inside its window is released
  // to the queue and sent by one drain now (Back, a link, a route change, a sign-out), never dropped.
  useEffect(() => () => {
    const open = Object.values(timersRef.current)
    timersRef.current = {}
    open.forEach((tm) => {
      window.clearTimeout(tm.commit)
      window.clearInterval(tm.tick)
    })
    if (open.length > 0) void releaseMoves(open.map((tm) => tm.move))
  }, [])

  const clearOrderTimers = useCallback((orderId: string): HeldMove | null => {
    const tm = timersRef.current[orderId]
    if (!tm) return null
    window.clearTimeout(tm.commit)
    window.clearInterval(tm.tick)
    delete timersRef.current[orderId]
    return tm.move
  }, [])

  // D2/action-doctrine: forward advance is ONE TAP, no dialog. Each card gets its own
  // undo window (a Map keyed by orderId, not a single global pending value) — a board
  // shows many orders at once, so locking every other card's advance button while one
  // card's undo window is open would be poor multi-card UX; this is the deliberate
  // per-card adaptation of Order-Detail's single-order undo-toast pattern.
  const handleAdvance = useCallback((orderId: string, toStatus: OrderStatusValue) => {
    if (sendingRef.current.has(orderId)) return
    const expectedStatus = dataRef.current?.items.find((o) => o.id === orderId)?.status
    if (!expectedStatus) return
    const replaced = clearOrderTimers(orderId)
    if (replaced) cancelMove(replaced)
    const move = holdMove(orderId, toStatus, expectedStatus)
    setPending((p) => ({ ...p, [orderId]: { toStatus, secondsLeft: UNDO_WINDOW_S, expectedStatus } }))

    const tick = window.setInterval(() => {
      setPending((p) => {
        const cur = p[orderId]
        if (!cur) return p
        return { ...p, [orderId]: { ...cur, secondsLeft: cur.secondsLeft - 1 } }
      })
    }, 1000)

    const commit = window.setTimeout(() => {
      window.clearInterval(tick)
      delete timersRef.current[orderId]
      setPending((p) => {
        const { [orderId]: _drop, ...rest } = p
        return rest
      })
      void takeMove(move).then((mine) => {
        if (mine) return sendMove(orderId, toStatus, expectedStatus)
      })
    }, UNDO_WINDOW_S * 1000)

    timersRef.current[orderId] = { commit, tick, move }
  }, [clearOrderTimers, sendMove])

  const handleUndo = useCallback((orderId: string) => {
    const move = clearOrderTimers(orderId)
    if (move) cancelMove(move)
    setPending((p) => {
      const { [orderId]: _drop, ...rest } = p
      return rest
    })
  }, [clearOrderTimers])

  // D2: Reject is irreversible — the Popover's reason entry + explicit submit tap IS the
  // confirm step (mirrors "confirm ONLY irreversible"); no undo window, commits immediately.
  const handleReject = useCallback((orderId: string, notes: string) => {
    if (sendingRef.current.has(orderId)) return
    const expectedStatus = dataRef.current?.items.find((o) => o.id === orderId)?.status
    void runWrite(
      orderId,
      () => rejectOrder(orderId, notes, expectedStatus),
      (res): Notice | null => (res.outcome === 'rejected' ? { kind: 'fail', orderId, message: res.message } : null),
    )
  }, [runWrite])

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'
  const pendingEntries = Object.entries(pending)
  const visibleItems = data ? data.items.filter((o) => !sentHold.has(o.id)) : []
  // ux2 (e): a toast names the order by the row's shortCode; a row no longer listed falls back
  // to formatOrderId's id tail (the same six characters, minted from it).
  const shortCodeOf = (orderId: string) => data?.items.find((o) => o.id === orderId)?.shortCode

  return (
    <div dir={dir} className={lang === 'ar' ? 'min-h-screen bg-background font-arabic' : 'min-h-screen bg-background font-sans'}>
      <CommandHeader title={bt(lang, 'title')} lang={lang} onLangToggle={onLangToggle} isOnline={isOnline} />

      <main className="mx-auto max-w-6xl space-y-4 px-4 py-6 pb-24">
        <RingPanel screen="board" lang={lang} />

        {/* Rook #9128 pre-B fix: Radix Tabs/ScrollArea each resolve their own `dir` via
            useDirection(dir) — defaulting to 'ltr' and stamping that explicitly on their
            own root DOM node when no `dir` prop is passed, which OVERRIDES the ambient
            RTL from the page's <div dir="rtl"> (an inherited CSS `direction` would have
            cascaded fine; an explicit dir="ltr" attribute on a descendant node wins over
            it). Passing dir={dir} on both roots — not just one — is required: the tab
            strip is Tabs.Root > ScrollArea.Root > TabsList, and either root left unfixed
            re-forces ltr on everything inside it. */}
        <Tabs dir={dir} value={statusFilter} onValueChange={(v) => chooseTab(v as StatusFilter)}>
          <ScrollArea dir={dir} className="w-full whitespace-nowrap">
            <TabsList className="inline-flex w-max">
              {STATUS_TABS.map((s) => (
                <TabsTrigger key={s} value={s}>
                  {s === 'ALL' ? bt(lang, 'statusAll') : STATUS_BADGE_LABEL[s][lang]}
                </TabsTrigger>
              ))}
            </TabsList>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </Tabs>

        {loadState === 'loading' && (
          <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
            {bt(lang, 'loading')}
          </div>
        )}

        {loadState === 'error' && (
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center">
            <p className="text-muted-foreground">{bt(lang, 'errorLoading')}</p>
            <Button size="lg" className="min-h-[48px]" onClick={() => load()}>
              {bt(lang, 'retry')}
            </Button>
          </div>
        )}

        {loadState === 'ready' && data && visibleItems.length === 0 && (
          <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
            {bt(lang, 'empty')}
          </div>
        )}

        {loadState === 'ready' && data && visibleItems.length > 0 && (
          // RTL-mirrored grid — `dir` on the root already flips grid auto-placement; no
          // extra rtl: overrides needed (design guidance, bench-HOP-ORDER-DETAIL.CB-A pattern).
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visibleItems.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                lang={lang}
                roles={roles}
                now={now}
                isNew={newIds.has(order.id)}
                pendingAdvance={pending[order.id]?.toStatus ?? null}
                sending={sending.has(order.id)}
                onAdvance={handleAdvance}
                onReject={handleReject}
                fact={facts?.get(order.id)}
                finalCheckState={fcRows[order.id]}
                onFinalCheck={handleFinalCheck}
              />
            ))}
          </div>
        )}
      </main>

      {/* Per-card undo toasts, stacked — a board can have multiple concurrent undo
          windows open (unlike the single-order Order-Detail screen), so this stacks
          rather than reusing OrderDetailScreen's single fixed-position UndoToast. */}
      {pendingEntries.map(([orderId, p], idx) => (
        <div
          key={orderId}
          role="status"
          className="fixed inset-x-4 z-40 mx-auto flex max-w-md items-center justify-between gap-4 rounded-xl border bg-foreground px-4 py-3 text-background shadow-lg sm:inset-x-auto sm:end-4"
          style={{ bottom: `${1 + idx * 4}rem` }}
        >
          <span className="text-sm font-medium">
            <Ltr>{formatOrderId(orderId, shortCodeOf(orderId))}</Ltr>{' '}
            {ot(lang, 'statusUpdated')} · <Ltr>{p.secondsLeft}s</Ltr>
          </span>
          <Button variant="secondary" size="sm" className="min-h-[44px] shrink-0" onClick={() => handleUndo(orderId)}>
            {ot(lang, 'undo')}
          </Button>
        </div>
      ))}

      {notice && (
        <div
          role={notice.kind === 'ok' ? 'status' : 'alert'}
          className="fixed inset-x-4 z-40 mx-auto flex min-h-[44px] max-w-md flex-wrap items-center justify-between gap-3 rounded-xl border bg-foreground px-4 py-3 text-background shadow-lg sm:inset-x-auto sm:end-4"
          style={{ bottom: `${1 + pendingEntries.length * 4}rem` }}
        >
          <span className="min-w-0 flex-1 break-words text-sm font-medium">
            {notice.kind === 'ok' && <CodedLine template={bt(lang, 'statusUpdatedFor')} orderId={notice.orderId} shortCode={shortCodeOf(notice.orderId)} />}
            {notice.kind === 'fail' && (
              <CodedLine template={bt(lang, 'advanceFailed')} orderId={notice.orderId} shortCode={shortCodeOf(notice.orderId)} slots={{ message: notice.message }} />
            )}
            {notice.kind === 'queuedRefused' && (
              <CodedLine template={bt(lang, 'queuedRefused')} orderId={notice.orderId} shortCode={shortCodeOf(notice.orderId)} slots={{ status: notice.status }} />
            )}
            {notice.kind === 'kitchen' && (
              <CodedLine template={bt(lang, 'kitchenOpen')} orderId={notice.orderId} shortCode={shortCodeOf(notice.orderId)} slots={{ count: notice.count }} />
            )}
          </span>
          <span className="flex shrink-0 gap-2">
            {notice.kind === 'kitchen' && (
              <Button
                variant="secondary"
                size="sm"
                className="min-h-[44px]"
                disabled={sending.has(notice.orderId)}
                onClick={() => void sendMove(notice.orderId, notice.toStatus, notice.expectedStatus, true)}
              >
                {bt(lang, 'moveAnyway')}
              </Button>
            )}
            <Button variant="secondary" size="sm" className="min-h-[44px]" onClick={() => setNotice(null)}>
              {bt(lang, 'dismiss')}
            </Button>
          </span>
        </div>
      )}
    </div>
  )
}
